import { useState } from 'react'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import { Modal } from '../UI/Modal/Modal'
import { Ds2026ButtonProvider } from '../FbUI/Ds2026ButtonContext'
import ReactSelectWrapper from '../FormUtils/ReactSelectWrapper'
import {
  DS2026_MODAL_BODY_CLASS,
  DS2026_MODAL_BUTTON_CLASS,
  DS2026_MODAL_CONTENT_CLASS,
  DS2026_MODAL_FOOTER_CLASS,
  DsInput,
  FieldGroup,
  FieldLabel,
  ds2026SelectStyles,
} from '../FbUI/Ds2026Field'
import {
  useUpdateSampleFulfillment,
  type UpdateSampleFulfillmentMutationRequest,
} from '../../services/samples'
import type { SampleCarrier } from '../../models/samples'

/** Carrier options for the tracking form. Mirrors SampleCarrier choices. */
const CARRIER_OPTIONS: { value: SampleCarrier; label: string }[] = [
  { value: 'dot_foods', label: 'DOT Foods' },
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'other', label: 'Other' },
]

export interface TrackingModalSample {
  id: number
  request_number: string
  tracking_number?: string
  tracking_carrier?: SampleCarrier | ''
  shipped_date?: string | null
}

interface SampleTrackingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sample: TrackingModalSample | null
  onSuccess?: () => void
}

/**
 * Edit a sample's manual tracking info — tracking number, carrier, and shipped
 * date (FIR-7033). Status is edited inline in the table; this modal owns the
 * tracking fields. Built on the DS2026 field kit (Ds2026Field) so it matches the
 * rest of the app's modals. Reset state by remounting via a `key` on the sample
 * id at the call site.
 */
export function SampleTrackingModal({
  open,
  onOpenChange,
  sample,
  onSuccess,
}: SampleTrackingModalProps) {
  const mutation = useUpdateSampleFulfillment()
  const [trackingNumber, setTrackingNumber] = useState(
    sample?.tracking_number ?? ''
  )
  const [carrier, setCarrier] = useState<SampleCarrier | ''>(
    sample?.tracking_carrier ?? ''
  )
  const [shippedDate, setShippedDate] = useState(sample?.shipped_date ?? '')

  const isSubmitting = mutation.isPending
  const carrierValue = CARRIER_OPTIONS.find((o) => o.value === carrier) ?? null

  const handleSubmit = async () => {
    if (!sample) return

    const data: UpdateSampleFulfillmentMutationRequest = {
      tracking_number: trackingNumber.trim(),
      // '' clears the carrier (backend allow_blank=True); the generated
      // BlankEnum resolves to `never`, so cast around the enum-only type.
      tracking_carrier:
        carrier as UpdateSampleFulfillmentMutationRequest['tracking_carrier'],
      shipped_date: shippedDate ? shippedDate : null,
    }

    const [err] = await to(mutation.mutateAsync({ id: sample.id, data }))

    if (err) {
      toast.error('Failed to update tracking info')
      return
    }
    toast.success('Tracking info updated')
    onSuccess?.()
    onOpenChange(false)
  }

  return (
    <Ds2026ButtonProvider>
      <Modal
        open={open}
        onOpenChange={(isOpen) =>
          !isOpen && !isSubmitting && onOpenChange(false)
        }
        title={<span className="text-neutral-800">Edit Tracking Info</span>}
        description={
          sample ? (
            <span className="text-neutral-500">
              Update tracking for sample request #{sample.request_number}.
            </span>
          ) : undefined
        }
        size="md"
        contentClassName={DS2026_MODAL_CONTENT_CLASS}
        closeIconClassName="rounded-lg hover:text-neutral-500"
        dialogFooterClassName={DS2026_MODAL_FOOTER_CLASS}
        onAccept={handleSubmit}
        acceptButtonText={isSubmitting ? 'Saving...' : 'Save'}
        blockAccept={isSubmitting}
        acceptButtonProps={{
          variant: 'primary',
          className: DS2026_MODAL_BUTTON_CLASS,
        }}
        cancelButtonText="Cancel"
        blockCancel={isSubmitting}
        cancelButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
      >
        <FieldGroup className={DS2026_MODAL_BODY_CLASS}>
          <div className="flex w-full flex-col gap-1.5">
            <FieldLabel htmlFor="tracking-number">Tracking Number</FieldLabel>
            <DsInput
              id="tracking-number"
              type="text"
              placeholder="1Z999AA10123456784"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              disabled={isSubmitting}
              maxLength={100}
            />
          </div>

          <div className="flex w-full flex-col gap-1.5">
            <FieldLabel>Carrier</FieldLabel>
            <ReactSelectWrapper
              ds2026
              styles={ds2026SelectStyles}
              options={CARRIER_OPTIONS}
              value={carrierValue}
              onChange={(option) =>
                setCarrier(
                  (option as { value: SampleCarrier } | null)?.value ?? ''
                )
              }
              isClearable
              isDisabled={isSubmitting}
              placeholder="Select a carrier"
            />
          </div>

          <div className="flex w-full flex-col gap-1.5">
            <FieldLabel htmlFor="shipped-date">Shipped Date</FieldLabel>
            <DsInput
              id="shipped-date"
              type="date"
              value={shippedDate ?? ''}
              onChange={(e) => setShippedDate(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </FieldGroup>
      </Modal>
    </Ds2026ButtonProvider>
  )
}
